import qs from "qs";
import { v4 as uuidv4 } from "uuid";
import { ApiPagedResponse } from "../model/api/types/apiTypes";

// Lib
import axios from "./axiosAdapter";

//App
import { getAccessToken } from "../../utility/tokenHelper";
import AuthService from "../../Auth/authService";
import { store } from "../../app/store";
import { setApiAlert } from "../../store/apiAlert";
import { AxiosRequestConfig } from "axios";
import { AxiosRequestConfiguration } from "../model/api/types/axiosRequestConfig";

class ApiClient {
  handleHttpErrorFn: any;
  requireAuth: any;
  authService: any;

  constructor() {
    // Private vars with public setters
    this.handleHttpErrorFn = null;
    this.requireAuth = true;
    this.authService = AuthService.getInstance();
  }

  // Public Setters
  setHandleHttpErrorFn(callback: any) {
    this.handleHttpErrorFn = callback;
  }

  _getFormattedErrorMessage = (errors: any) => {
    return errors?.map((error: any) => error).join(", ");
  };

  setRequireAuth(requireAuth: any) {
    this.requireAuth = requireAuth;
  }

  // Private Methods
  _setAuthTokenHeader(authToken: any) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
  }

  _deleteAuthTokenHeader() {
    delete axios.defaults.headers.common["Authorization"];
  }

  _handleSuccess<T>(
    resp: any,
    resolve400Response: boolean = false
  ): Promise<T> | any {
    if (resp?.messages?.length === 0) {
      return resp;
    }

    const updatedMessage = resp?.messages?.map((message: any) => {
      return {
        ...message,
        show: message.message !== null && message.message !== "",
        id: uuidv4(),
      };
    });

    if (resp.operationStatusCode === 400 && !resolve400Response) {
      store.dispatch(setApiAlert({ alerts: updatedMessage }));
      return Promise.reject(resp);
    } else if (resolve400Response) {
      return resp;
    } else {
      if (updatedMessage !== undefined)
        store.dispatch(setApiAlert({ alerts: updatedMessage }));
      return resp;
    }
  }

  _handleError<T>(
    originalRequestConfig: any,
    error: any,
    customErrorFunc: any
  ): Promise<T> | any {
    return new Promise((resolve, reject) => {
      if (error.response?.status === 401) {
        this.refreshTheAccessToken()
          .then(() => {
            axios(originalRequestConfig)
              .then((resp) => {
                resolve(resp);
              })
              .catch((err) => {
                if (customErrorFunc) {
                  customErrorFunc(err);
                } else {
                  this.handleHttpErrorFn?.(err.response);
                }
                reject(err);
              });
          })
          .catch((tokenError) => this.handleHttpErrorFn?.(tokenError));

        resolve(error.message);
      } else {
        if (customErrorFunc) {
          customErrorFunc(error);
        } else {
          this.handleHttpErrorFn?.(error.response);
        }
        reject(error);
      }
    });
  }

  _makeCall<T>(
    config: AxiosRequestConfiguration,
    customErrorFunc: ((error: any) => any) | undefined
  ): Promise<T> {
    const call = (): Promise<T> => {
      this.requireAuth && this.checkAuth();

      return new Promise<T>((resolve, reject) => {
        axios(config)
          .then((resp) => {
            resolve(this._handleSuccess<T>(resp, config?.resolve400Response));
          })
          .catch((error) => {
            this._handleError<T>(config, error, customErrorFunc)
              .then((resp: any) => resolve(this._handleSuccess<T>(resp)))
              .catch((error: any) => reject(error));
          });
      });
    };

    return call();
  }

  // Public methods
  checkAuth() {
    const token = getAccessToken();

    return new Promise((resolve, reject) => {
      if (token) {
        this._setAuthTokenHeader(token);
        resolve(true);
      } else {
        this._deleteAuthTokenHeader();
        reject(false);
      }
    });
  }

  refreshTheAccessToken() {
    this._deleteAuthTokenHeader();
    return new Promise<void>((resolve, reject) => {
      this.authService
        .signinSilent()
        .then(() => {
          const accessToken = getAccessToken();
          this._setAuthTokenHeader(accessToken);

          resolve();
        })
        .catch((e: any) => {
          window.location.reload();
          reject(e);
        });
    });
  }

  get<T>(
    url: string,
    params: Record<string, any> = {},
    customErrorFunc?: ((error: any) => any) | undefined,
    headers?: Record<string, any>
  ): Promise<T> {
    const config = {
      method: "GET",
      url,
      params,
      paramsSerializer: (params: any) =>
        qs.stringify(params, { arrayFormat: "repeat" }),
      headers,
    };

    return this._makeCall<T>(config, customErrorFunc);
  }

  post<T, U = T>(
    url: string,
    body?: T,
    resolve400Response?: boolean,
    customErrorFunc?: ((error: any) => any) | undefined,
    headers?: Record<string, any>,
    responseType?: AxiosRequestConfig["responseType"]
  ): Promise<ApiPagedResponse<U>> {
    const config: AxiosRequestConfiguration = {
      method: "POST",
      url,
      data: body,
      headers,
      responseType,
      resolve400Response,
    };
    return this._makeCall(config, customErrorFunc);
  }

  put<T>(
    url: string,
    body: any,
    customErrorFunc?: ((error: any) => any) | undefined
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url,
      data: body,
    };

    return this._makeCall(config, customErrorFunc);
  }

  delete<T>(
    url: string,
    params: Record<string, any> = {},
    customErrorFunc?: ((error: any) => any) | undefined
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url,
      params: params,
      paramsSerializer: function (params: any) {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
    };

    return this._makeCall(config, customErrorFunc);
  }
}

// Create an instance of ApiClient
const apiClient = new ApiClient();

export default apiClient;
